import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CalendarCheck, AlertTriangle } from 'lucide-react';
import { Card } from '../../../ui/card';
import { Button } from '../../../ui/button';
import { Input } from '../../../ui/input';
import { CALL_THEMES, formatCallDate, groupCallsByMonth } from './callCalendarUtils';

const toggleSet = (set, value) => {
  const next = new Set(set);
  if (next.has(value)) next.delete(value);
  else next.add(value);
  return next;
};

const CalendarRolloverSection = ({ adminFetch, styles }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [flipped, setFlipped] = useState(new Set()); // dates Kevin changed from the draft's default
  const [superBowl, setSuperBowl] = useState('');
  const [confirming, setConfirming] = useState(false);

  const load = useCallback(async () => {
    if (!adminFetch) return;
    setLoading(true);
    setError('');
    try {
      const response = await adminFetch('/admin/calendar-rollover');
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.detail || `Failed to load the yearly calendar (${response.status})`);
      setData(payload);
    } catch (loadError) {
      setError(loadError.message || 'Failed to load the yearly calendar');
    } finally {
      setLoading(false);
    }
  }, [adminFetch]);

  useEffect(() => { load(); }, [load]);

  const rollover = data?.rollover || null;
  const year = rollover?.year;

  // The draft as Kevin currently sees it: the default, plus his changes, plus the Super Bowl date.
  const entries = useMemo(() => {
    if (!rollover || rollover.status !== 'draft') return [];
    return (rollover.dates || []).map((entry) => {
      const defaultIncluded = entry.status === 'included';
      const changeable = entry.theme !== null; // fifth Sundays can never be added
      let included = changeable && flipped.has(entry.date) ? !defaultIncluded : defaultIncluded;
      let reason = entry.reason;
      if (superBowl && entry.date === superBowl) {
        included = false;
        reason = 'Super Bowl Sunday';
      }
      return { ...entry, included, reason: included ? null : reason || 'Removed by you', changeable };
    });
  }, [rollover, flipped, superBowl]);

  const months = useMemo(() => groupCallsByMonth(entries), [entries]);
  const includedDates = entries.filter((entry) => entry.included).map((entry) => entry.date);

  const post = async (path, body, failureMessage) => {
    setBusy(true);
    setError('');
    try {
      const response = await adminFetch(path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.detail || `${failureMessage} (${response.status})`);
      setData(payload);
      setConfirming(false);
      setFlipped(new Set());
      return true;
    } catch (postError) {
      setError(postError.message || failureMessage);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const submit = () => post(
    `/admin/calendar-rollover/${year}/submit`,
    { dates: includedDates, super_bowl_date: superBowl || null },
    'Could not submit the calendar'
  );
  const confirmSuperBowl = () => post(
    `/admin/calendar-rollover/${year}/super-bowl`,
    { super_bowl_date: superBowl },
    'Could not confirm the Super Bowl date'
  );

  if (loading && !data) return null;

  return (
    <Card className="p-6" data-testid="calendar-rollover">
      <h3 className={`${styles.sectionTitle} mb-1 flex items-center gap-2`}>
        <CalendarCheck className="h-5 w-5 text-aqua" />
        {year ? `${year} calendar` : 'Next year’s calendar'}
      </h3>

      {error ? (
        <div className="mb-4 rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
          {error}
          <Button type="button" variant="outline" size="sm" className="ml-3" onClick={load}>Try again</Button>
        </div>
      ) : null}

      {!rollover ? (
        <p className={styles.bodyText}>
          Next year&apos;s Sunday calendar is drafted automatically on August 1. You will get an email asking you to review it.
        </p>
      ) : null}

      {rollover?.status === 'existing' ? (
        <p className={styles.bodyText}>This year is already on the calendar.</p>
      ) : null}

      {rollover?.status === 'draft' ? (
        <>
          <p className={`${styles.bodyText} mb-4`}>
            Every Sunday of {year}, with holidays already removed and a focus area assigned. Remove anything unusual,
            add the Super Bowl date, then press Submit. After you submit, update the Zoom recurring series to match.
          </p>
          <div className="mb-4 flex flex-wrap items-end gap-3">
            <label className={`${styles.subtleText} flex flex-col gap-1`} htmlFor="super-bowl-date">
              Super Bowl Sunday (February)
              <Input id="super-bowl-date" aria-label="Super Bowl date" type="date" value={superBowl}
                onChange={(event) => setSuperBowl(event.target.value)} />
            </label>
            <p className={`${styles.subtleText} max-w-md`}>
              Look this up and enter it. If you leave it blank, the February Sundays stay on hold (nobody can book them)
              until you confirm it.
            </p>
          </div>

          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {months.map((month) => (
              <div key={month.key} className={`${styles.panel} p-3`}>
                <p className={`${styles.sectionTitle} mb-2 text-sm`}>{month.label}</p>
                <ul className="space-y-1.5">
                  {month.calls.map((entry) => {
                    const theme = CALL_THEMES[entry.theme];
                    return (
                      <li key={entry.date} className="flex items-center justify-between gap-2 text-sm">
                        <span className={`flex flex-wrap items-center gap-1.5 ${entry.included ? '' : 'text-gray-400 line-through'}`}>
                          {formatCallDate(entry.date, { month: 'short', day: 'numeric' })}
                          {entry.included && theme ? (
                            <span className={`rounded-md border px-1.5 text-xs ${theme.chip}`}>{theme.label}</span>
                          ) : null}
                          {entry.included && entry.flag ? (
                            <span className="flex items-center gap-1 text-xs text-amber-600 no-underline">
                              <AlertTriangle className="h-3 w-3" /> {entry.flag}
                            </span>
                          ) : null}
                          {!entry.included ? <span className="text-xs no-underline">{entry.reason}</span> : null}
                        </span>
                        {entry.changeable && !(superBowl && entry.date === superBowl) ? (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            aria-label={`${entry.included ? 'Remove' : 'Add back'} ${formatCallDate(entry.date, { month: 'long', day: 'numeric' })}`}
                            onClick={() => setFlipped((current) => toggleSet(current, entry.date))}
                          >
                            {entry.included ? 'Remove' : 'Add back'}
                          </Button>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-3">
            {confirming ? (
              <>
                <Button type="button" disabled={busy} onClick={submit}>
                  {busy ? 'Publishing...' : `Yes, publish ${includedDates.length} calls and email Guide Partners`}
                </Button>
                <Button type="button" variant="outline" disabled={busy} onClick={() => setConfirming(false)}>Not yet</Button>
              </>
            ) : (
              <Button type="button" disabled={busy || includedDates.length === 0} onClick={() => setConfirming(true)}>
                Submit {year} calendar ({includedDates.length} calls)
              </Button>
            )}
          </div>
        </>
      ) : null}

      {rollover?.status === 'published' ? (
        <>
          <p className={`${styles.bodyText} mb-4`} data-testid="published-note">
            The {year} calendar is live ({rollover.published_event_count ?? 0} calls added
            {rollover.published_by === 'auto' ? ', published automatically because it was not reviewed' : ''}).
          </p>

          {!rollover.super_bowl_date ? (
            <div className="mb-4 rounded-xl border border-amber-400 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:bg-amber-500/10 dark:text-amber-200"
              data-testid="super-bowl-hold">
              <p className="flex items-center gap-2 font-semibold">
                <AlertTriangle className="h-4 w-4" /> The Super Bowl date is not confirmed.
              </p>
              <p className="mt-1">
                February Sundays are on hold, so Guide Partners cannot book them. Enter the date to remove that Sunday
                from the calendar and open the others.
              </p>
              <div className="mt-3 flex flex-wrap items-end gap-3">
                <Input aria-label="Super Bowl date" type="date" value={superBowl} onChange={(event) => setSuperBowl(event.target.value)} />
                <Button type="button" disabled={busy || !superBowl} onClick={confirmSuperBowl}>Confirm Super Bowl date</Button>
              </div>
            </div>
          ) : (
            <p className={`${styles.subtleText} mb-4`}>
              Super Bowl Sunday: {formatCallDate(rollover.super_bowl_date, { month: 'long', day: 'numeric', year: 'numeric' })}.
            </p>
          )}

          <div
            className={`rounded-xl border px-4 py-3 text-sm ${
              (data.missing_guides || []).length === 0
                ? 'border-emerald-400 bg-emerald-50 text-emerald-800 dark:bg-emerald-500/10 dark:text-emerald-200'
                : 'border-amber-400 bg-amber-50 text-amber-900 dark:bg-amber-500/10 dark:text-amber-200'
            }`}
            data-testid="missing-guides"
          >
            {(data.missing_guides || []).length === 0 ? (
              <p className="font-semibold">Every Guide Partner has picked a {year} date.</p>
            ) : (
              <>
                <p className="font-semibold">
                  {data.missing_guides.length} Guide Partner{data.missing_guides.length === 1 ? ' has' : 's have'} not picked a {year} date yet:
                </p>
                <ul className="mt-2 space-y-0.5">
                  {data.missing_guides.map((guide) => (
                    <li key={guide.user_id || guide.email}>{guide.name || guide.email} ({guide.email})</li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </>
      ) : null}
    </Card>
  );
};

export default CalendarRolloverSection;
